import React, { useCallback, useEffect, useRef, useState } from "react";
import { Pause, Play, X } from "lucide-react";
import { motion, PanInfo } from "motion/react";
import { ITrack } from "@agp/shared.user-app/module/guide-player.module/audio-client";
import { useLanguageBasedFont } from "../const/font-utils";

type MonotoneMiniAudioPlayerProps = {
  track: ITrack | null;
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  onPlayPause: () => void;
  onSeek: (time: number) => void;
  onNext?: () => void;
  onTouchStart: (e: React.TouchEvent) => void;
  onTouchMove: (e: React.TouchEvent) => void;
  onTouchEnd: (e: React.TouchEvent) => void;
  onMouseDown: (e: React.MouseEvent) => void;
  opacity: number;
  pointerEvents: "auto" | "none";
  onClose?: () => void;
};

const MonotoneMiniAudioPlayer: React.FC<MonotoneMiniAudioPlayerProps> = ({
  track,
  isPlaying,
  currentTime,
  duration,
  onPlayPause,
  onSeek,
  onNext,
  onTouchStart,
  onTouchMove,
  onTouchEnd,
  onMouseDown,
  opacity,
  pointerEvents,
  onClose,
}) => {
  // プログレスバー用の状態
  const [isSeekDragging, setIsSeekDragging] = useState(false);
  const [currentSeekRate, setCurrentSeekRate] = useState(0);
  const sliderRef = useRef<HTMLDivElement>(null);
  const progressBarRef = useRef<HTMLDivElement>(null);
  const fontClass = useLanguageBasedFont();

  const clamp = (num: number, min: number, max: number) =>
    Math.min(Math.max(num, min), max);

  // プログレスバーのドラッグ機能
  const getSeekRate = useCallback((clientX: number) => {
    const progressBarRect = progressBarRef.current?.getBoundingClientRect();
    if (!progressBarRect) return 0;
    return clamp(
      (clientX - progressBarRect.left) / progressBarRect.width,
      0,
      1
    );
  }, []);

  const onSeekDragStart = useCallback(
    (e: MouseEvent | TouchEvent | PointerEvent, _: PanInfo) => {
      e.preventDefault();
      e.stopPropagation();
      setIsSeekDragging(true);
    },
    []
  );

  const onSeekDrag = useCallback(
    (e: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
      e.preventDefault();
      e.stopPropagation();
      const rate = getSeekRate(info.point.x);
      setCurrentSeekRate(rate);
      setIsSeekDragging(true);
    },
    [getSeekRate]
  );

  const onSeekDragEnd = useCallback(
    (_: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
      const seekRate = getSeekRate(info.point.x);
      onSeek(duration * seekRate);
      setCurrentSeekRate(seekRate);
      setTimeout(() => {
        setIsSeekDragging(false);
      }, 100);
    },
    [getSeekRate, onSeek, duration]
  );

  // プログレスバーの位置を更新
  useEffect(() => {
    if (!isSeekDragging && duration !== 0) {
      const seekRate = currentTime / duration;
      if (seekRate === 0) {
        setCurrentSeekRate(0);
        return;
      }
      setCurrentSeekRate(seekRate);
      setCurrentSeekRate((x) => {
        const diffMilliseconds = Math.abs(x * duration - currentTime) * 1000;
        return diffMilliseconds < 500 ? seekRate : x;
      });
    }
  }, [duration, currentTime, isSeekDragging]);

  useEffect(() => {
    setCurrentSeekRate(0);
  }, [track]);

  // クリックでのシーク機能
  const handleProgressChange = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = progressBarRef.current?.getBoundingClientRect();
    if (!rect) return;

    const clickX = e.clientX - rect.left;
    const newTime = (clickX / rect.width) * duration;
    onSeek(Math.floor(newTime));
  };

  return (
    <>
      <div
        className="absolute inset-0 flex flex-col items-center p-3 pt-6 transition-opacity duration-300"
        style={{
          opacity: opacity,
          pointerEvents: pointerEvents,
        }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onMouseDown={onMouseDown}
      >
        <div className={"flex w-full justify-between items-start gap-4 pr-4"}>
          {/* 閉じるボタン */}
          <button
            className={"px-2 text-gray-500"}
            style={{
              opacity: opacity,
              pointerEvents: pointerEvents,
            }}
            onClick={(e) => {
              e.stopPropagation();
              onClose?.();
            }}
          >
            <X size={30} />
          </button>
          <div className={"flex gap-4 items-center"}>
            <button
              className="p-2 rounded-full bg-[#F2F2F2] text-black border-[1px] border-solid border-black"
              onClick={(e) => {
                e.stopPropagation();
                onPlayPause();
              }}
            >
              {isPlaying ? (
                <Pause className={"w-[25px] h-[25px]"} fill="black" />
              ) : (
                <Play className={"w-[25px] h-[25px]"} fill="black" />
              )}
            </button>

            <button className="p-2 rounded-full bg-[#F2F2F2] border-[1px] border-solid border-black text-[10px]">
              <div
                className={`w-[25px] h-[25px] text-center flex items-center justify-center text-[13px] ${fontClass}`}
                onClick={() => {
                  onNext?.();
                }}
              >
                next
              </div>
            </button>
          </div>
        </div>

        <div className="px-4 w-full gap-3 flex-shrink-0 flex-grow mt-1">
          <div className="flex-1 min-w-0 flex flex-col justify-between h-full">
            <div className="text-[14px] text-gray-700 line-clamp-2">
              {track?.name}
            </div>

            {/* 透明なインプット領域（操作性向上のため） */}
            <div
              id="monotone-progress-bar"
              className="relative w-full py-4 cursor-pointer flex-shrink-0"
              data-draggable="progress"
              ref={sliderRef}
            >
              {/* 実際のプログレスバー（見た目） */}
              <div
                className="relative w-full h-2 bg-gray-200 rounded-full"
                ref={progressBarRef}
              >
                <div
                  className="h-full bg-[#877F87] rounded-full transition-all duration-0"
                  style={{ width: `${currentSeekRate * 100}%` }}
                  data-progress-fill="true"
                ></div>
              </div>

              {/* 透明なタッチ領域 */}
              <motion.div
                className="absolute inset-0 w-full h-full"
                drag="x"
                dragElastic={false}
                dragConstraints={progressBarRef}
                dragMomentum={false}
                onDragStart={onSeekDragStart}
                onDrag={onSeekDrag}
                onDragEnd={onSeekDragEnd}
                onClick={handleProgressChange}
                data-progress-handle="true"
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MonotoneMiniAudioPlayer;
